"use client";

import * as React from "react";
import { Check, Copy } from "lucide-react";

import { copyRichTextToClipboard, copyTextToClipboard } from "@/lib/clipboard";
import { Button, type ButtonProps } from "./button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const COPIED_RESET_MS = 2000;

/** Plain text, or rich content that keeps its formatting when pasted into a doc. */
export type CopyValue = string | { html: string; plainText: string };

export interface CopyOptions {
  /** Toast on success (default "Copied to clipboard"); null when the copied state is feedback enough. */
  successMessage?: string | null;
  /** Toast on failure; null to stay silent (the text is selectable). */
  errorMessage?: string | null;
  /** Runs after the content reached the clipboard (analytics). */
  onCopied?: () => void;
}

/**
 * Copies and remembers it for two seconds (`copied`), toasting the outcome. Call `copy`
 * synchronously inside the click: Safari refuses a clipboard write after an await.
 */
export function useCopyToClipboard({ successMessage, errorMessage, onCopied }: CopyOptions = {}) {
  const [copied, setCopied] = React.useState(false);
  const resetRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (resetRef.current) clearTimeout(resetRef.current);
    },
    []
  );
  const copy = React.useCallback(
    async (value: CopyValue): Promise<boolean> => {
      const ok =
        typeof value === "string"
          ? await copyTextToClipboard(value, { successMessage, errorMessage })
          : await copyRichTextToClipboard({ ...value, successMessage, errorMessage });
      if (!ok) return false;
      setCopied(true);
      if (resetRef.current) clearTimeout(resetRef.current);
      resetRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
      onCopied?.();
      return true;
    },
    [successMessage, errorMessage, onCopied]
  );
  return { copy, copied };
}

export interface CopyButtonProps extends Omit<ButtonProps, "value" | "onClick" | "children" | "asChild">, CopyOptions {
  /** What to copy; a function runs inside the click, so content built on demand still copies in Safari. */
  value: CopyValue | (() => CopyValue | null);
  /** The button's accessible name and tooltip ("Copy link"). */
  label: string;
}

/** An icon button that copies, swaps its icon to a check for two seconds, and toasts the outcome. */
const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  ({ value, label, successMessage, errorMessage, onCopied, variant = "quiet", size = "icon-xs", ...props }, ref) => {
    const { copy, copied } = useCopyToClipboard({ successMessage, errorMessage, onCopied });
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant={variant}
            size={size}
            aria-label={copied ? "Copied" : label}
            onClick={() => {
              const content = typeof value === "function" ? value() : value;
              if (content !== null) void copy(content);
            }}
            {...props}
          >
            {copied ? <Check className="text-success" aria-hidden="true" /> : <Copy aria-hidden="true" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied" : label}</TooltipContent>
      </Tooltip>
    );
  }
);
CopyButton.displayName = "CopyButton";

export { CopyButton };
